export interface ErrorStrategy {
  recovery: string;
  userMessage: string;
  canProceed: boolean;
  requiresConfirmation?: boolean;
}

export interface SectionErrorStrategy {
  recovery: string;
  userMessage: string;
  maxRetries?: number;
  requiresConfirmation?: boolean;
}

export const ERROR_STRATEGIES: Record<string, ErrorStrategy> = {
  MISSING_WRITING_STYLE: {
    recovery: 'use_default_style',
    userMessage: 'No writing style defined. Using default contemporary prose.',
    canProceed: true
  },
  MISSING_CHARACTERS: {
    recovery: 'user_must_create_characters',
    userMessage: 'No characters defined. Please create at least one character before generating.',
    canProceed: false
  },
  MISSING_PLOT: {
    recovery: 'proceed_without_plot',
    userMessage: 'No plot defined. Chapter will be generated without plot guidance.',
    canProceed: true,
    requiresConfirmation: true
  },
  MISSING_WORLD: {
    recovery: 'proceed_without_world',
    userMessage: 'No world defined. Chapter will lack world-specific details.',
    canProceed: true,
    requiresConfirmation: true
  },
  INSUFFICIENT_CHARACTER_DATA: {
    recovery: 'use_character_summaries_only',
    userMessage: 'Some characters lack detailed profiles. Dialogue quality may suffer.',
    canProceed: true,
    requiresConfirmation: true
  },
  CONTEXT_TOO_LARGE: {
    recovery: 'auto_truncate_context',
    userMessage: 'Too much context. The AI will use only the most relevant information.',
    canProceed: true
  }
};

export const SECTION_ERROR_STRATEGIES: Record<string, SectionErrorStrategy> = {
  SECTION_TOO_SHORT: {
    recovery: 'regenerate_with_expanded_prompt',
    userMessage: 'Generated section is shorter than the requested length. Consider regenerating with additional detail.',
    maxRetries: 1
  },
  SECTION_TOO_LONG: {
    recovery: 'truncate_or_regenerate',
    userMessage: 'Generated section exceeded the requested length. Consider regenerating or manually trimming.',
    maxRetries: 1
  },
  SECTION_CONTINUITY_BREAK: {
    recovery: 'user_decision_required',
    userMessage: 'Generated section may not flow from surrounding text. Review before inserting.',
    requiresConfirmation: true
  },
  SECTION_VOICE_MISMATCH: {
    recovery: 'regenerate_with_voice_emphasis',
    userMessage: 'Generated section may not match the established character voice. Consider regenerating with stronger voice guidance.',
    maxRetries: 1
  },
  CONCURRENT_GENERATION_BLOCKED: {
    recovery: 'wait_for_active_generation',
    userMessage: 'Another section generation is already running. Please wait for it to finish before starting a new one.'
  },
  GENERATION_FAILED: {
    recovery: 'user_decision_required',
    userMessage: 'Section generation failed unexpectedly. Review context and try again after resolving any issues.'
  }
};

